import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const [form, setForm]       = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.username || !form.password) { setError('Please enter your credentials.'); return; }
    setLoading(true); setError('');
    try {
      const userData = await login(form.username, form.password);
      navigate(userData.must_change_password ? '/settings?tab=password' : '/');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid username or password.');
    }
    setLoading(false);
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f0f4fb',
      padding: '24px',
    }}>
      {/* Subtle grid background */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0,
        backgroundImage: `
          linear-gradient(rgba(27,63,148,.04) 1px, transparent 1px),
          linear-gradient(90deg, rgba(27,63,148,.04) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
      }} />

      {/* Glow blobs */}
      <div style={{ position: 'fixed', top: '-10%', left: '-5%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(27,63,148,.10) 0%, transparent 70%)', zIndex: 0, pointerEvents: 'none' }} />
      <div style={{ position: 'fixed', bottom: '-10%', right: '-5%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,.08) 0%, transparent 70%)', zIndex: 0, pointerEvents: 'none' }} />

      {/* Card */}
      <div className="fade-in" style={{
        position: 'relative', zIndex: 1,
        width: '100%', maxWidth: 420,
        background: '#ffffff',
        borderRadius: 24,
        boxShadow: '0 4px 6px rgba(27,63,148,.04), 0 24px 80px rgba(27,63,148,.12)',
        border: '1px solid rgba(27,63,148,.08)',
        overflow: 'hidden',
      }}>

        {/* Top accent bar */}
        <div style={{ height: 4, background: 'linear-gradient(90deg, #1B3F94, #3B82F6, #6366F1)' }} />

        <div style={{ padding: '44px 40px 40px' }}>

          {/* Logo mark */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 36 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 12,
              background: 'linear-gradient(135deg, #1B3F94 0%, #2a5bc4 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(27,63,148,.35)',
              flexShrink: 0,
            }}>
              {/* Simple shield / compliance icon */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l7 4v5c0 4.5-3 8.5-7 10C5 19.5 2 15.5 2 11V6l10-4z"/>
                <polyline points="9 12 11 14 15 10"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.1 }}>Compliance Hub</div>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', marginTop: 1 }}>Report Reminders</div>
            </div>
          </div>

          {/* Heading */}
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', margin: 0, lineHeight: 1.2 }}>
              Sign in
            </h1>
            <p style={{ color: '#64748b', fontSize: 13.5, marginTop: 6 }}>
              Enter your credentials to access your workspace.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              marginBottom: 20, padding: '10px 14px',
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 10, fontSize: 13, color: '#dc2626',
              display: 'flex', alignItems: 'center', gap: 8,
              animation: 'fadeIn .25s ease',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Username */}
            <div>
              <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: '#334155', marginBottom: 7, letterSpacing: '.02em' }}>
                Username
              </label>
              <input
                value={form.username}
                onChange={e => { setForm(f => ({ ...f, username: e.target.value })); setError(''); }}
                placeholder="your.username"
                autoComplete="username"
                autoFocus
                style={{
                  width: '100%', padding: '11px 14px', fontSize: 14,
                  background: '#f8faff', border: '1.5px solid #e2e8f0',
                  borderRadius: 10, outline: 'none', color: '#0f172a',
                  transition: 'border-color .2s, box-shadow .2s',
                  fontFamily: 'var(--font)',
                }}
                onFocus={e => { e.target.style.borderColor = '#1B3F94'; e.target.style.boxShadow = '0 0 0 3px rgba(27,63,148,.12)'; e.target.style.background = '#fff'; }}
                onBlur={e  => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; e.target.style.background = '#f8faff'; }}
              />
            </div>

            {/* Password */}
            <div>
              <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: '#334155', marginBottom: 7, letterSpacing: '.02em' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => { setForm(f => ({ ...f, password: e.target.value })); setError(''); }}
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                  style={{
                    width: '100%', padding: '11px 44px 11px 14px', fontSize: 14,
                    background: '#f8faff', border: '1.5px solid #e2e8f0',
                    borderRadius: 10, outline: 'none', color: '#0f172a',
                    transition: 'border-color .2s, box-shadow .2s',
                    fontFamily: 'var(--font)',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#1B3F94'; e.target.style.boxShadow = '0 0 0 3px rgba(27,63,148,.12)'; e.target.style.background = '#fff'; }}
                  onBlur={e  => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; e.target.style.background = '#f8faff'; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(v => !v)}
                  style={{
                    position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#94a3b8', padding: 2, display: 'flex',
                    transition: 'color .15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = '#475569'}
                  onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: 6,
                width: '100%', padding: '12px',
                background: loading
                  ? '#93a3c4'
                  : 'linear-gradient(135deg, #1B3F94 0%, #2a5bc4 100%)',
                border: 'none', borderRadius: 11,
                color: '#fff', fontSize: 14.5, fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : '0 4px 16px rgba(27,63,148,.30)',
                transition: 'all .2s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
                letterSpacing: '-0.01em',
                fontFamily: 'var(--font)',
              }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.filter = 'brightness(1.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}}
              onMouseLeave={e => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'none'; }}
            >
              {loading ? (
                <>
                  <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,.4)', borderTop: '2px solid #fff', borderRadius: '50%' }} className="spin" />
                  Signing in…
                </>
              ) : 'Sign In'}
            </button>
          </form>

          {/* Footer note */}
          <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
            <span style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 500 }}>
                        </span>
          </div>
        </div>
      </div>
    </div>
  );
}
